import * as React from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/* =========================
   Estilos centralizados
   ========================= */
const getWidthString = (columnWidth) =>
  typeof columnWidth === "number" ? `${columnWidth}px` : String(columnWidth);

const containerSxBase = (gap) => ({
  display: "flex",
  alignItems: "flex-start",
  gap, // usa theme.spacing(gap)
  flexWrap: "nowrap",
  width: "100%",
});

const columnSxBase = (gap, columnWidth) => {
  const w = getWidthString(columnWidth);
  return {
    display: "flex",
    flexDirection: "column",
    gap,
    flex: `0 0 ${w}`,
    maxWidth: w,
  };
};

// camada off-screen para medir itens
const measureLayerSx = {
  position: "absolute",
  visibility: "hidden",
  pointerEvents: "none",
  overflow: "hidden",
  top: -10000,
  left: -10000,
};

/* =========================
   Utilitários
   ========================= */

/** Converte "25%", "18rem", "320px" ou number (px) para px */
function toPx(value, containerWidth) {
  if (typeof value === "number") return value;
  const v = String(value).trim().toLowerCase();

  if (v.endsWith("%")) return (parseFloat(v) / 100) * containerWidth;
  if (v.endsWith("rem")) return parseFloat(v) * 16; // ajuste se seu root font-size for outro
  if (v.endsWith("px")) return parseFloat(v);

  const num = parseFloat(v);
  return Number.isFinite(num) ? num : 0;
}

/** Divide um array em N colunas por blocos sequenciais (1–4, 5–8, 9–12...) */
function splitIntoColumns(arr, cols) {
  const perCol = Math.ceil(arr.length / cols);
  return Array.from({ length: cols }, (_, i) =>
    arr.slice(i * perCol, (i + 1) * perCol)
  );
}

/** Mede a largura do container com ResizeObserver */
function useContainerWidth() {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState(0); // 1) useState

  // 2) useMemo — (não há memos aqui)

  // 3) useEffect
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const w = entry?.contentRect?.width ?? el.getBoundingClientRect().width;
      setWidth(w);
    });

    ro.observe(el);
    // valor inicial
    setWidth(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

/** Balanceamento por altura respeitando minItemsPerColumn */
function assignByHeight(countCols, itemHeights, minItemsPerColumn) {
  const N = itemHeights.length;
  const colHeights = Array(countCols).fill(0);
  const colCounts = Array(countCols).fill(0);
  const colIndices = Array.from({ length: countCols }, () => []);

  for (let i = 0; i < N; i++) {
    const needMin = colCounts.some((c) => c < minItemsPerColumn);

    let chosen = 0;
    if (needMin) {
      // escolhe coluna com menor contagem; em empate, menor altura
      let bestCount = Infinity;
      let bestHeight = Infinity;
      for (let c = 0; c < countCols; c++) {
        if (
          colCounts[c] < bestCount ||
          (colCounts[c] === bestCount && colHeights[c] < bestHeight)
        ) {
          bestCount = colCounts[c];
          bestHeight = colHeights[c];
          chosen = c;
        }
      }
    } else {
      // escolhe coluna mais "leve"
      let best = Infinity;
      for (let c = 0; c < countCols; c++) {
        if (colHeights[c] < best) {
          best = colHeights[c];
          chosen = c;
        }
      }
    }

    colIndices[chosen].push(i);
    colCounts[chosen] += 1;
    colHeights[chosen] += itemHeights[i] ?? 0;
  }

  return colIndices;
}

/* =========================
   Helpers responsivos
   ========================= */

/**
 * Resolve um valor responsivo baseado em breakpoints do MUI.
 * Aceita:
 *  - number (retorna direto)
 *  - objeto: { xs?, sm?, md?, lg?, xl? }
 * Regra: usa o MAIOR breakpoint "up" que estiver ativo e definido no objeto.
 */
function useResponsiveNumber(value, fallback) {
  const theme = useTheme();
  const up = {
    xs: useMediaQuery(theme.breakpoints.up("xs")),
    sm: useMediaQuery(theme.breakpoints.up("sm")),
    md: useMediaQuery(theme.breakpoints.up("md")),
    lg: useMediaQuery(theme.breakpoints.up("lg")),
    xl: useMediaQuery(theme.breakpoints.up("xl")),
  };

  return React.useMemo(() => {
    if (typeof value === "number") return value;
    if (!value || typeof value !== "object") return fallback;

    const order = ["xl", "lg", "md", "sm", "xs"]; // maior primeiro
    for (const bp of order) {
      if (up[bp] && typeof value[bp] === "number") return value[bp];
    }
    // fallback: primeiro definido no objeto (ordem crescente)
    const fallbackOrder = ["xs", "sm", "md", "lg", "xl"];
    for (const bp of fallbackOrder) {
      if (typeof value[bp] === "number") return value[bp];
    }
    return fallback;
  }, [value, up.xs, up.sm, up.md, up.lg, up.xl, fallback]);
}

/* =========================
   Componente principal
   ========================= */

/**
 * Props:
 * - children
 * - columnWidth: number | string ("25%")
 * - gap
 * - minColumns / maxColumns
 * - minItemsPerColumn: number | { xs?, sm?, md?, lg?, xl? }
 * - distributeExtraEvenly:
 *      quando true, NÃO limita colunas pelo total de itens (via mínimo);
 *      reserva o mínimo nas primeiras colunas e joga o restante na ÚLTIMA.
 *      Ex.: 10 itens, 3 colunas, min=4 -> 4 | 4 | 2
 */
export default function ResponsiveColumnsBalanced({
  children,
  columnWidth = 320,
  gap = 2,
  minColumns = 1,
  maxColumns = Infinity,
  minItemsPerColumn = 1, // pode ser number ou objeto de breakpoints
  distributeExtraEvenly = false,
  containerSx,
  columnSx,
}) {
  const theme = useTheme();
  const [ref, width] = useContainerWidth(); // mede o container

  // 1) useState
  const [itemHeights, setItemHeights] = React.useState([]); // alturas medidas (px)
  const measureRefs = React.useRef([]);

  // 2) useMemo
  const childArray = React.useMemo(
    () => React.Children.toArray(children),
    [children]
  );

  const gapPx = React.useMemo(() => {
    const s = theme.spacing(gap); // e.g. "16px"
    const n = Number.parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }, [gap, theme]);

  // Resolve o mínimo atual com base nos breakpoints
  const activeMinItems = useResponsiveNumber(minItemsPerColumn, 1);

  const cols = React.useMemo(() => {
    if (!width) return 1;
    const cw = toPx(columnWidth, width);
    if (!cw) return 1;

    let n = Math.floor((width + gapPx) / (cw + gapPx));
    n = Math.max(minColumns, Math.min(maxColumns, n));

    if (!distributeExtraEvenly) {
      // modo padrão (balanceio por altura): limita colunas pelo mínimo de itens/coluna
      const maxColsByItems =
        Math.floor(childArray.length / Math.max(1, activeMinItems)) || 1;
      n = Math.min(n, maxColsByItems);
    }
    return Math.max(1, n);
  }, [
    width,
    columnWidth,
    gapPx,
    minColumns,
    maxColumns,
    childArray.length,
    distributeExtraEvenly,
    activeMinItems,
  ]);

  const columnWidthPx = React.useMemo(
    () => toPx(columnWidth, width),
    [columnWidth, width]
  );

  const heightsReady = React.useMemo(() => {
    return (
      itemHeights.length === childArray.length &&
      itemHeights.every((h) => Number.isFinite(h) && h > 0)
    );
  }, [itemHeights, childArray.length]);

  const columns = React.useMemo(() => {
    if (!cols) return [];

    if (distributeExtraEvenly) {
      // ✅ Modo pedido: reserva 'activeMinItems' nas primeiras (cols - 1) colunas; resto vai na última
      const result = Array.from({ length: cols }, () => []);
      let idx = 0;

      for (let c = 0; c < cols - 1 && idx < childArray.length; c++) {
        const take = Math.min(activeMinItems, childArray.length - idx);
        result[c] = childArray.slice(idx, idx + take);
        idx += take;
      }
      if (idx < childArray.length) {
        result[cols - 1] = childArray.slice(idx);
      }
      return result;
    }

    // modo balanceado por altura (padrão)
    if (heightsReady) {
      const indicesPerCol = assignByHeight(cols, itemHeights, activeMinItems);
      return indicesPerCol.map((idxs) => idxs.map((i) => childArray[i]));
    }

    // fallback inicial: blocos sequenciais (evita piscar)
    return splitIntoColumns(childArray, cols);
  }, [
    cols,
    childArray,
    heightsReady,
    itemHeights,
    distributeExtraEvenly,
    activeMinItems,
  ]);

  // 3) useEffect — medir alturas
  React.useEffect(() => {
    if (!columnWidthPx || childArray.length === 0) {
      setItemHeights([]);
      return;
    }
    let raf = requestAnimationFrame(() => {
      const hs = measureRefs.current
        .slice(0, childArray.length)
        .map((el) => (el ? el.offsetHeight : 0));
      setItemHeights(hs);
    });
    return () => cancelAnimationFrame(raf);
  }, [childArray.length, columnWidthPx]);

  return (
    <>
      {/* Camada de medição (off-screen) */}
      <Box sx={{ ...measureLayerSx, width: columnWidthPx || 0 }}>
        {childArray.map((node, i) => (
          <Box
            key={`measure-${i}`}
            ref={(el) => (measureRefs.current[i] = el)}
            sx={{ width: "100%" }}
          >
            {node}
          </Box>
        ))}
      </Box>

      {/* Layout real */}
      <Box ref={ref} sx={{ ...containerSxBase(gap), ...containerSx }}>
        {columns.map((colChildren, colIdx) => (
          <Box
            key={colIdx}
            sx={{ ...columnSxBase(gap, columnWidth), ...columnSx }}
          >
            {colChildren}
          </Box>
        ))}
      </Box>
    </>
  );
}
